
// Common disposable email domains to block
const DISPOSABLE_EMAIL_DOMAINS = [
  '10minutemail.com',
  'guerrillamail.com',
  'mailinator.com',
  'tempmail.org',
  'throwaway.email',
  'getnada.com',
  'temp-mail.org',
  'maildrop.cc',
  'yopmail.com',
  'trashmail.com',
  'dispostable.com',
  'spam4.me',
  'emailondeck.com',
  'fakeinbox.com',
  'sharklasers.com'
];

// Trusted email domain patterns (major providers)
const TRUSTED_DOMAIN_PATTERNS = [
  /^gmail\.com$/,
  /^yahoo\.com$/,
  /^outlook\.com$/,
  /^hotmail\.com$/,
  /^icloud\.com$/,
  /^protonmail\.com$/,
  /^.*\.edu$/,  // Educational institutions
  /^.*\.gov$/,  // Government domains
  /^.*\.org$/   // Organizations (with caution)
];

export interface EmailValidationResult {
  isValid: boolean;
  domain: string;
  reason?: string;
  riskLevel: 'low' | 'medium' | 'high';
}

export function validateEmailDomain(email: string): EmailValidationResult {
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      domain: '',
      reason: 'Invalid email format',
      riskLevel: 'high'
    };
  }

  const domain = email.split('@')[1].toLowerCase();

  // Check for disposable email domains
  if (DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
    return {
      isValid: false,
      domain,
      reason: 'Disposable email addresses are not allowed',
      riskLevel: 'high'
    };
  }

  // Check for suspicious patterns
  if (domain.includes('temp') || domain.includes('fake') || domain.includes('trash')) {
    return {
      isValid: false,
      domain,
      reason: 'Suspicious email domain detected',
      riskLevel: 'high'
    };
  }

  // Check for very short domains (potential typos or suspicious)
  if (domain.length < 4) {
    return {
      isValid: false,
      domain,
      reason: 'Domain name too short',
      riskLevel: 'medium'
    };
  }

  // Check if it's a trusted domain
  const isTrusted = TRUSTED_DOMAIN_PATTERNS.some(pattern => pattern.test(domain));
  
  if (isTrusted) {
    return {
      isValid: true,
      domain,
      riskLevel: 'low'
    };
  }

  // For other domains, allow but mark as medium risk
  return {
    isValid: true,
    domain,
    riskLevel: 'medium'
  };
}

export function getEmailDomainValidationMessage(result: EmailValidationResult): string {
  if (!result.isValid && result.reason) {
    return result.reason;
  }

  switch (result.riskLevel) {
    case 'high':
      return 'This email domain is not allowed';
    case 'medium':
      return 'Please verify your email address carefully';
    case 'low':
    default:
      return '';
  }
}
